// Basketball — Products. Ported from the CLAUDE DESIGN prototype
// (bb-products.jsx): Hardwood Slip, POWERBALL, First Half Intelligence, Q1 Strike
// (shared PoolScreen), The Night Shift, Spread Fade. Each screen is wired to its
// real read hook (useHardwoodSlip / usePowerball / useFirstHalf / useQ1 /
// useBasketballBoard / useSpreadFade). HONEST by construction: every screen
// renders its dormant ghost preview when the hook is null / rest / empty;
// loading → <Loading/>; error → error pill + dormant beneath. No mock data.
//
// Exports a ProductScreen({section}) dispatcher (consumed by BasketballPage) plus
// each screen for direct use.

import { useState } from "react";
import {
  AwaitPanel, BbHead, BIcon, CompoundStair, Loading,
  fmtOdds, fmtPct, fmtTip, fmtLine, type BasketballSection, type StairLeg,
} from "./_bbkit.tsx";
import { BfpeDrawer, type BfpeDrawerData } from "./BfpeDrawer.tsx";
import { GameCard } from "./SlateScreen.tsx";
import { useBasketballBoard, type BballGame } from "./useBasketballBoard.ts";
import {
  useHardwoodSlip, usePowerball, useFirstHalf, useQ1, useSpreadFade,
  type FadeSignal, type SlotBoard, type MarketCandidate,
} from "./useBasketballProducts.ts";

// Confidence string → 0-5 pip count for the confidence meter.
function confPips(c: string | null | undefined): number {
  const u = (c ?? "").toUpperCase();
  if (u === "HIGH") return 5;
  if (u === "MEDIUM" || u === "MED") return 3;
  if (u === "LOW") return 2;
  return 0;
}

function ConfPips({ n, asBars = false }: { n: number; asBars?: boolean }): JSX.Element {
  if (asBars) {
    return (
      <div style={{ display: "flex", gap: 3, justifyContent: "flex-end", marginTop: 8 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} style={{ width: 7, height: 16, background: i < n ? "var(--bb-orange)" : "var(--bg-inset)", borderRadius: 1 }} />
        ))}
      </div>
    );
  }
  return (
    <div className="conf">
      {Array.from({ length: 5 }).map((_, k) => <span key={k} className={`p ${k < n ? "on" : ""}`} />)}
    </div>
  );
}

// ── Hardwood Slip ───────────────────────────────────────────────
export function HardwoodSlipScreen(): JSX.Element {
  const { loading, error, data } = useHardwoodSlip();
  const dormant = !loading && (error != null || !data || data.rest || data.legs.length === 0);

  return (
    <div className="bb" data-screen-label="hardwood-slip">
      <BbHead kicker="HARDWOOD SLIP" title="Basketball" />
      {loading ? (
        <Loading label="Loading the slip…" />
      ) : dormant ? (
        <>
          {error ? <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load the slip" body={error} ghosts={4} /> : null}
          <div className="bb-await-grand">
            <div className="k">RESTING TODAY · HARDWOOD SLIP</div>
            <h3>Slip rests when the math says rest.</h3>
            <p>{data?.reason || "The Hardwood Slip rests when no five-leg combination clears both the edge floor and the small-odds band. We sit a slip out before we force one."}</p>
            <div className="bb-ghost-stair" aria-hidden="true">
              {[18, 30, 46, 64, 84].map((h, i) => (
                <div key={i} className="gh-step">
                  <div className="gh-bar" style={{ height: `${h}%` }} />
                  <div className="gh-lab">Leg {i + 1}</div>
                </div>
              ))}
              <div className="gh-overlay">PREVIEW · not live</div>
            </div>
          </div>
        </>
      ) : (
        (() => {
          const slip = data!;
          const stairLegs: StairLeg[] = slip.legs.map((l) => ({ cum: l.cumulative_odds.toFixed(2), odds: fmtOdds(l.book_odds) }));
          return (
            <>
              <div className="bb-product-summary">
                <div className="head">
                  SUMMARY
                  <span className="t">{slip.leg_count} low-odds legs · combined {slip.combined_odds != null ? slip.combined_odds.toFixed(2) : "—"}× · {slip.in_target_band ? "in the small-odds band" : "below the small-odds band"}</span>
                </div>
                <div className="stat">POOL SIZE<span className="v">{slip.pool_size}</span></div>
                <div className="stat acc">COMBINED<span className="v">{slip.combined_odds != null ? slip.combined_odds.toFixed(2) : "—"}<span style={{ fontSize: 18 }}>×</span></span></div>
                <div className="stat">BAND<span className="v" style={{ fontSize: 20, color: slip.in_target_band ? "var(--bb-won)" : "var(--text-tertiary)" }}>{slip.in_target_band ? "IN ✓" : "OUT"}</span></div>
              </div>

              <CompoundStair legs={stairLegs} />

              <div className="bb-legs-table">
                <div className="bb-legs-header">
                  <div>#</div>
                  <div>SELECTION</div>
                  <div style={{ textAlign: "right" }}>ODDS</div>
                  <div style={{ textAlign: "right" }}>CUM ×</div>
                  <div style={{ textAlign: "right" }}>CONFIDENCE</div>
                </div>
                {slip.legs.map((l, i) => (
                  <div key={`${l.game_id}-${i}`} className="bb-legs-row">
                    <div className="idx">{i + 1}</div>
                    <div>
                      <div className="selection">{l.selection}</div>
                      <div className="market">{l.market_code}</div>
                    </div>
                    <div className="odds">{fmtOdds(l.book_odds)}</div>
                    <div className="cum">{l.cumulative_odds.toFixed(2)}×</div>
                    <ConfPips n={confPips(l.confidence)} />
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 16, fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-quaternary)", display: "flex", justifyContent: "space-between" }}>
                <span>Pool of {slip.pool_size} candidates{slip.computed_at ? ` · computed ${fmtTip(slip.computed_at)}` : ""}</span>
                <span>Pinnacle-anchored · BFPE-verified</span>
              </div>
            </>
          );
        })()
      )}
    </div>
  );
}

// ── POWERBALL ───────────────────────────────────────────────────
function DefLine({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 13 }}>
      <span style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase" }}>{k}</span>
      <span style={{ color: "var(--text)", fontWeight: 500 }}>{v}</span>
    </div>
  );
}

export function PowerballScreen(): JSX.Element {
  const { loading, error, data } = usePowerball();
  const rest = !loading && (error != null || !data || data.rest || data.legs.length === 0);

  return (
    <div className="bb" data-screen-label="powerball">
      <BbHead kicker="POWERBALL" title="Basketball" />

      {loading ? <Loading label="Loading POWERBALL…" /> : null}

      {!loading && rest ? (
        <>
          {error ? <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load POWERBALL" body={error} ghosts={4} /> : null}
          <div className="bb-await-grand">
            <div className="k">REST WEEK · POWERBALL</div>
            <h3>The compound rests when the math says rest.</h3>
            <p>Calibration sample below threshold. POWERBALL only fires when the weekly compound math clears every BIG 6 sign-off — six legs, target band 80–120×, every close-line history checked.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, marginTop: 26 }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-quaternary)", display: "flex", justifyContent: "space-between" }}>
                <span>What a published POWERBALL looks like</span>
                <span>preview · not live</span>
              </div>
              <div className="bb-await-pb-preview">
                {[
                  { x: "1.85×", h: 18 }, { x: "3.50×", h: 32 }, { x: "6.65×", h: 48 },
                  { x: "13.0×", h: 64 }, { x: "25.4×", h: 82 }, { x: "48.3×", h: 100 },
                ].map((b, i) => (
                  <div key={i} className="gb" style={{ height: `${b.h}%` }} data-x={b.x} />
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6, fontFamily: "var(--font-mono)", fontSize: 9.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-quaternary)", textAlign: "center" }}>
                <span>Leg 1</span><span>Leg 2</span><span>Leg 3</span><span>Leg 4</span><span>Leg 5</span><span>Leg 6</span>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {!loading && !rest ? (
        (() => {
          const pb = data!;
          const stairLegs: StairLeg[] = pb.legs.map((l) => ({ cum: l.cumulative_odds != null ? l.cumulative_odds.toFixed(2) : "—", odds: fmtOdds(l.book_odds ?? null) }));
          return (
            <>
              <div className="bb-product-summary">
                <div className="head">SUMMARY<span className="t">{pb.leg_count} legs · combined {pb.combined_odds != null ? pb.combined_odds.toFixed(0) : "—"}× for the week</span></div>
                <div className="stat">POOL SIZE<span className="v">{pb.pool_size}</span></div>
                <div className="stat acc">COMBINED<span className="v">{pb.combined_odds != null ? pb.combined_odds.toFixed(0) : "—"}<span style={{ fontSize: 18 }}>×</span></span></div>
                <div className="stat">LEGS<span className="v">{pb.leg_count}</span></div>
              </div>
              <CompoundStair legs={stairLegs} />
              <div className="bb-legs-table">
                <div className="bb-legs-header">
                  <div>#</div><div>SELECTION</div>
                  <div style={{ textAlign: "right" }}>ODDS</div>
                  <div style={{ textAlign: "right" }}>CUM ×</div>
                  <div style={{ textAlign: "right" }}>CONFIDENCE</div>
                </div>
                {pb.legs.map((l, i) => (
                  <div key={`${l.game_id ?? i}-${i}`} className="bb-legs-row">
                    <div className="idx">{i + 1}</div>
                    <div>
                      <div className="selection">{l.selection ?? "—"}</div>
                      <div className="market">{l.market_code ?? "—"}</div>
                    </div>
                    <div className="odds">{fmtOdds(l.book_odds ?? null)}</div>
                    <div className="cum">{l.cumulative_odds != null ? `${l.cumulative_odds.toFixed(2)}×` : "—"}</div>
                    <ConfPips n={confPips(l.confidence)} />
                  </div>
                ))}
              </div>
            </>
          );
        })()
      ) : null}

      <div className="bb-section-title" style={{ marginTop: 28 }}>
        <h3>What POWERBALL is</h3>
        <div className="sub">Weekly · compounded · earned, not assembled</div>
      </div>

      <div className="bb-grid-2">
        <div className="bb-card">
          <div className="bb-card-body">
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.18em", color: "var(--bb-orange-text)", textTransform: "uppercase", marginBottom: 14 }}>Weekly architecture</div>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                "Six legs · weekly cadence (Mon → Sat)",
                "Each leg compounds the prior · target band 80-120×",
                "No leg < 1.65 · no leg > 2.20",
                "One BIG 6 trigger per leg · convergence preferred",
                "Slip-live publication only if every leg clears CLV history",
              ].map((line, i) => (
                <li key={i} style={{ display: "grid", gridTemplateColumns: "24px 1fr", gap: 12, fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--bb-orange-text)", letterSpacing: "0.08em" }}>0{i + 1}</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bb-card">
          <div className="bb-card-body">
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.18em", color: "var(--text-tertiary)", textTransform: "uppercase", marginBottom: 14 }}>Next eligible window</div>
            <div style={{ fontFamily: "var(--bb-display)", fontSize: 64, color: "var(--text)", lineHeight: 0.95, letterSpacing: "0.01em", fontVariantNumeric: "tabular-nums" }}>Mon</div>
            <div style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 6 }}>06:00 GMT · the assembler reads Sunday close.</div>
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--border-faint)", display: "flex", flexDirection: "column", gap: 8 }}>
              <DefLine k="Pool target" v="80 – 120×" />
              <DefLine k="Leg count" v="6" />
              <DefLine k="Sign-off" v="All 6 personas" />
              <DefLine k="Last published" v="— · in calibration" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── First Half / Q1 — shared PoolScreen ─────────────────────────
function poolDrawer(p: MarketCandidate, family: string): BfpeDrawerData {
  return {
    pick: `${p.selection} · ${p.market_code}`,
    fair: "—", book: "—", edge: fmtPct(p.value_pct ?? null),
    confidence: p.confidence ? p.confidence.toUpperCase() : "—",
    why: `${family} edge — surfaced from the day's qualified card. The full pricing detail appears once basketball is live.`,
  };
}

function PoolScreen({ section, kicker, leadCopy, emptyCopy, board, family }: {
  section: BasketballSection; kicker: string; leadCopy: string; emptyCopy: string;
  board: { loading: boolean; error: string | null; data: SlotBoard | null }; family: string;
}): JSX.Element {
  const { loading, error, data } = board;
  const populated = !!data && (data.pick != null || data.pool.length > 0);

  return (
    <div className="bb" data-screen-label={section}>
      <BbHead kicker={kicker} title="Basketball" />
      <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 22, maxWidth: "64ch", lineHeight: 1.55 }}>{leadCopy}</p>

      {loading ? (
        <Loading label="Loading…" />
      ) : !populated ? (
        <>
          {error ? <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load" body={error} ghosts={4} /> : null}
          <div className="bb-await-grand">
            <div className="k">RESTING · {kicker}</div>
            <h3>{emptyCopy}</h3>
            <p>Empty is honest. We do not fabricate a pick when the math does not back it. The pool re-runs every 30 minutes during a live slate window.</p>
            <div className="bb-ghost-pool" aria-hidden="true">
              <div className="gh-top">
                <div className="gh-top-l"><div className="gh-tag" /><div className="gh-title" /></div>
                <div className="gh-top-r"><div className="gh-stat" /><div className="gh-stat" /><div className="gh-stat" /></div>
              </div>
              <div className="gh-rows">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="gh-row">
                    <div className="gh-idx" /><div className="gh-mid"><div /><div /></div><div className="gh-edge" />
                  </div>
                ))}
              </div>
              <div className="gh-overlay">PREVIEW · top pick + pool fills here</div>
            </div>
          </div>
        </>
      ) : (
        <>
          {data!.pick ? (
            <>
              <div className="bb-section-title">
                <h3>Top pick</h3>
                <div className="sub">Highest BFPE edge in window</div>
              </div>
              <div className="bb-card" style={{ marginBottom: 22 }}>
                <div className="bb-card-body" style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 24, alignItems: "baseline" }}>
                  <div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.14em", color: "var(--bb-orange-text)", textTransform: "uppercase", marginBottom: 6 }}>{data!.pick!.market_code}</div>
                    <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em" }}>{data!.pick!.selection}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-tertiary)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Edge</div>
                    <div style={{ fontFamily: "var(--bb-display)", fontSize: 32, color: "var(--bb-won)", marginTop: 2 }}>{fmtPct(data!.pick!.value_pct ?? null)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-tertiary)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Confidence</div>
                    <ConfPips n={confPips(data!.pick!.confidence)} asBars />
                  </div>
                </div>
                <div className="bb-card-body" style={{ paddingTop: 0 }}>
                  <BfpeDrawer data={poolDrawer(data!.pick!, family)} defaultLabel={kicker} />
                </div>
              </div>
            </>
          ) : null}

          {data!.pool.length > 0 ? (
            <>
              <div className="bb-section-title">
                <h3>Candidate pool</h3>
                <div className="sub">{data!.pool_size} considered · {data!.pool.length} above threshold</div>
              </div>
              <div className="bb-legs-table">
                <div className="bb-legs-header">
                  <div>#</div><div>SELECTION</div>
                  <div style={{ textAlign: "right" }}>EDGE</div>
                  <div style={{ textAlign: "right" }}>CONF</div>
                  <div style={{ textAlign: "right" }} />
                </div>
                {data!.pool.map((p, i) => (
                  <div key={`${p.game_id}-${i}`} className="bb-legs-row">
                    <div className="idx">{i + 1}</div>
                    <div>
                      <div className="selection">{p.selection}</div>
                      <div className="market">{p.market_code}</div>
                    </div>
                    <div className="cum" style={{ color: "var(--bb-won)" }}>{fmtPct(p.value_pct ?? null)}</div>
                    <ConfPips n={confPips(p.confidence)} />
                    <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-tertiary)" }}>BFPE →</div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

export function FirstHalfScreen(): JSX.Element {
  return (
    <PoolScreen
      section="first-half"
      kicker="FIRST HALF INTELLIGENCE"
      leadCopy="First-half markets close earlier and move harder. We hunt one top pick and a deeper candidate pool — the rest waits for the second half tape."
      emptyCopy="No first-half edges cleared the threshold today."
      board={useFirstHalf()}
      family="first-half"
    />
  );
}

export function Q1Screen(): JSX.Element {
  return (
    <PoolScreen
      section="q1-strike"
      kicker="Q1 STRIKE"
      leadCopy="Q1-only markets — opening tip energy, opening-five usage, referee inflation, pace-against-pace. The narrowest window, the loudest signal."
      emptyCopy="No Q1 edges cleared the threshold today. Q1 is the tightest filter we run."
      board={useQ1()}
      family="first-quarter"
    />
  );
}

// ── The Night Shift ─────────────────────────────────────────────
const WA_LATE_START = 19; // 19:00 local and later = the night window
export function NightShiftScreen(): JSX.Element {
  const { loading, error, games } = useBasketballBoard();
  const night = games.filter((g) => {
    if (!g.tipoffAt) return false;
    const d = new Date(g.tipoffAt);
    return !Number.isNaN(d.getTime()) && d.getHours() >= WA_LATE_START;
  });
  const empty = !loading && (error != null || night.length === 0);

  return (
    <div className="bb" data-screen-label="night-shift">
      <BbHead kicker="THE NIGHT SHIFT" title="Basketball" />

      <div style={{ position: "relative", marginBottom: 24, padding: "32px 36px", background: "var(--bg-elev)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
        <div style={{ position: "absolute", right: 36, top: 36, opacity: 0.4 }}>
          <BIcon name="moon" size={56} color="var(--bb-orange-text)" />
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.2em", color: "var(--bb-orange-text)", textTransform: "uppercase", marginBottom: 12 }}>West Africa · after 19:00 local</div>
        <h2 style={{ fontFamily: "var(--bb-display)", fontSize: 44, margin: 0, letterSpacing: "0.01em", fontWeight: 400 }}>The board, after dark.</h2>
        <p style={{ maxWidth: "52ch", color: "var(--text-secondary)", fontSize: 14, marginTop: 12, lineHeight: 1.55 }}>
          The Night Shift filters the slate down to tip-offs that fall inside the West-Africa evening
          window — late NBA, EuroLeague, NBL. Same odds, same close-line tracking, narrower lens.
        </p>
      </div>

      {loading ? (
        <Loading label="Loading the night slate…" />
      ) : empty ? (
        <>
          {error ? <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load the night slate" body={error} ghosts={4} /> : null}
          <div className="bb-await-grand">
            <div className="k">EMPTY WINDOW · THE NIGHT SHIFT</div>
            <h3>No late tips in window.</h3>
            <p>Tonight's slate finishes before the West-Africa evening window opens. Check back at the start of NBA primetime — the lens narrows but the rules don't change.</p>
            <div className="bb-ghost-night" aria-hidden="true">
              <svg viewBox="0 0 200 50" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="bb-nightg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--bb-orange-soft)" />
                    <stop offset="100%" stopColor="transparent" />
                  </linearGradient>
                </defs>
                <rect x="0" y="0" width="200" height="50" fill="url(#bb-nightg)" opacity="0.5" />
                <g fontFamily="var(--font-mono)" fontSize="3" fill="var(--text-tertiary)" letterSpacing="0.4">
                  {["18:00", "19:00", "20:00", "21:00", "22:00", "23:00", "00:00", "01:00"].map((t, i) => (
                    <text key={i} x={4 + i * 28} y={48}>{t}</text>
                  ))}
                </g>
                <line x1="56" x2="56" y1="6" y2="44" stroke="var(--bb-orange)" strokeWidth="0.5" strokeDasharray="1 1" opacity="0.5" />
                <text x={56} y="4" fontFamily="var(--font-mono)" fontSize="2.5" fill="var(--bb-orange-text)" textAnchor="middle" letterSpacing="0.3">WINDOW OPENS · 19:00 LOCAL</text>
              </svg>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="bb-section-title">
            <h3>Late tips</h3>
            <div className="sub">{night.length} games in the night window</div>
          </div>
          <div className="bb-slate">
            {night.map((g: BballGame) => <GameCard key={g.fixtureId} game={g} />)}
          </div>
        </>
      )}
    </div>
  );
}

// ── Spread Fade ─────────────────────────────────────────────────
function fadeDrawer(s: FadeSignal): BfpeDrawerData {
  const confidence = s.alert_level === "CRITICAL" ? "HIGH" : s.alert_level === "HIGH" ? "MEDIUM" : "LOW";
  return {
    pick: s.fade_direction,
    fair: fmtLine(s.fair_spread, true),
    book: fmtLine(s.book_spread, true),
    edge: fmtPct(s.expected_edge_pct),
    confidence,
    why: `${s.fatigued_team} on the back end of a road back-to-back vs ${s.opponent} on full rest. Book spread has not fully priced the rest gap.`,
    agentsLabel: "Fatigue agents",
    agents: [
      { label: "Fatigue gauge", note: `score ${s.fatigue_score.toFixed(1)} (${s.gauge}) · ${s.alert_level}`, tone: "edge" },
      { label: "BFPE", note: `fair ${fmtLine(s.fair_spread, true)} vs book ${fmtLine(s.book_spread, true)} · gap ${s.spread_gap}`, tone: "edge" },
      { label: "Model fade-cover", note: fmtProbLike(s.fade_cover_prob), tone: "edge" },
    ],
    fairLineLabel: "Fair price vs book",
    fairLine: `Fair spread projects ${Math.abs(s.spread_gap)} points off the book line. Market has not adjusted for travel + roster context.`,
    clv: `Line opened at ${fmtLine(s.book_spread, true)} — tracking for the CLV ledger.`,
  };
}

function fmtProbLike(p: number | null | undefined): string {
  return typeof p === "number" && Number.isFinite(p) ? `${(p * 100).toFixed(1)}%` : "—";
}

export function SpreadFadeScreen(): JSX.Element {
  const { loading, error, data } = useSpreadFade();
  const empty = !loading && (error != null || !data || data.signal_count === 0);

  return (
    <div className="bb" data-screen-label="spread-fade">
      <BbHead kicker="SPREAD FADE" title="Basketball" />

      {loading ? (
        <Loading label="Loading fade signals…" />
      ) : empty ? (
        <>
          {error ? <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load the fade board" body={error} ghosts={4} /> : null}
          <div className="bb-await-grand">
            <div className="k">NO SIGNALS · SPREAD FADE</div>
            <h3>No fade signals today.</h3>
            <p>Fade signals fire when fatigue, schedule density, and a soft book spread converge. None on the board cleared the floor. The slate runs clean.</p>
            <div className="bb-ghost-gauge" aria-hidden="true">
              <div className="gh-gauge-head"><span>Fatigue score</span><span className="gh-val">— <span className="gh-out">/ 10</span></span></div>
              <div className="gh-gauge-segs">{Array.from({ length: 10 }).map((_, i) => <div key={i} className="gh-seg" />)}</div>
              <div className="gh-gauge-band"><span>LOW</span><span>MID</span><span>HIGH</span><span>CRIT</span></div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="bb-section-title">
            <h3>Fatigue map</h3>
            <div className="sub">{data!.signal_count} signals · {data!.signals.filter((s) => s.alert_level === "CRITICAL").length} critical · ROAD B2B · TRIPLE FATIGUE · TOTAL FADE</div>
          </div>

          <div className="bb-fade-list">
            {data!.signals.map((s, i) => {
              const gscore = Math.round(s.fatigue_score);
              return (
                <div key={`${s.game_id}-${i}`} className="bb-fade">
                  <div className="bb-fade-meta">
                    <div className="signal-type">{s.signal_type.replace(/_/g, " ")}</div>
                    <div className="teams">{s.fatigued_team} <span className="vs">vs</span> {s.opponent}</div>
                    <div className="fatigued">fatigued · <span className="t">{s.fatigued_team}</span></div>
                    <div className={`alert ${s.alert_level.toLowerCase() === "elevated" ? "elev" : s.alert_level.toLowerCase()}`}>
                      <span className="dot" />{s.alert_level} alert
                    </div>
                  </div>

                  <div className="bb-gauge">
                    <div className="bb-gauge-head"><span>Fatigue score</span><span className="val">{s.fatigue_score.toFixed(1)}<span className="out"> / 10</span></span></div>
                    <div className="bb-gauge-segs">
                      {Array.from({ length: 10 }).map((_, k) => {
                        const idx = k + 1;
                        let band = "";
                        if (idx <= gscore) {
                          if (idx <= 5) band = "b-low";
                          else if (idx <= 7) band = "b-mid";
                          else if (idx <= 9) band = "b-hi";
                          else band = "b-crit";
                        }
                        return <div key={k} className={`bb-gauge-seg ${band ? `on ${band}` : ""}`} />;
                      })}
                    </div>
                    <div className="bb-gauge-band"><span>1</span><span /><span /><span /><span>5</span><span /><span>7</span><span /><span>9</span><span>10</span></div>
                  </div>

                  <div className="bb-fade-verdict">
                    <div className="row"><span>Fade direction</span><span className="v" style={{ color: "var(--bb-orange-text)" }}>{s.fade_direction}</span></div>
                    <div className="row"><span>Fair / Book</span><span className="v">{fmtLine(s.fair_spread, true)} <span style={{ color: "var(--text-tertiary)", fontWeight: 400 }}>· {fmtLine(s.book_spread, true)}</span></span></div>
                    <div className="row"><span>Spread gap</span><span className="v gap">{s.spread_gap}</span></div>
                    <div className="row"><span>Model fade-cover</span><span className="v pos">{fmtProbLike(s.fade_cover_prob)}</span></div>
                    <div style={{ marginTop: 6 }}>
                      <BfpeDrawer data={fadeDrawer(s)} defaultLabel="FATIGUE BREAKDOWN" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Dispatcher ──────────────────────────────────────────────────
/** Dispatch a product section to its screen. Returns null for non-product sections. */
export function ProductScreen({ section }: { section: BasketballSection }): JSX.Element | null {
  switch (section) {
    case "hardwood-slip": return <HardwoodSlipScreen />;
    case "spread-fade": return <SpreadFadeScreen />;
    case "powerball": return <PowerballScreen />;
    case "first-half": return <FirstHalfScreen />;
    case "q1-strike": return <Q1Screen />;
    case "night-shift": return <NightShiftScreen />;
    default: return null;
  }
}
